import { useHistory, useParams } from 'react-router-dom';
import styles from './MultiCollections.module.scss';
import { useGetMultiCollectionLazyQuery, useSaveMultiCollectionsMutation } from '@api/graphql-codegen'
import { useCallback, useEffect, useState } from 'react';
import Header from '@src/components/header/Header';
import NormalModal from '@src/components/modal/normal-modal/NormalModal';
import Button from '@src/components/button/Button';
import { useQueryParams, useToast } from '@src/hooks';
import Spinner from '@src/components/spinner/Spinner';
import CustomizeMultiCollections from '@src/components/collection/CustomizeMultiCollections';

type Props = {
  id: string;
}

type Params = { id: string; }

function MultiCollectionCustomize(props: Props) {
  const { id } = useParams<Params>();
  const params = useQueryParams();
  const programId = params.get('program');
  const history = useHistory();
  const { successToast } = useToast();

  const [saveModal, setSaveModal] = useState(false) 
  const [savingLessons, setSavingLessons] = useState<any[]>([])

  const navigateToPage = useCallback((id) => {
    if (programId) {
      history.push('/super/program-management', { id: programId, mcId: id });
    } else {
      history.push('/super/multi-collections', { id });
    }
  }, [history, programId]);

  const [getMultiCollection, {data: currentCollection, loading: collectionLoading}] = useGetMultiCollectionLazyQuery({
    fetchPolicy: 'network-only'});

  useEffect(() => {
    if (id) {
      getMultiCollection({
        variables: { input: { id: id } }
      });
    }
  }, [getMultiCollection, id])

  const [saveMultiCollection, {loading}] = useSaveMultiCollectionsMutation({
    onCompleted(data) {
      if(data.saveMultiCollections) {
        successToast("Collection has been updated successfully!");
      }
      setSaveModal(false);
      navigateToPage(id);
    },
  });

  const handleSave = useCallback((lessons: any[]) => {
    const collectionContents = lessons.map((lesson, index) => {
      return {
        id: lesson.id,
        multiCollectionId: id,
        type: lesson.type,
        position: index + 1
      }
    })

    saveMultiCollection({
      variables: {
        input: collectionContents
      }
    })
  }, [id, saveMultiCollection]);

  const handleExit = useCallback((collections: any[]) => {
    if (currentCollection?.getMultiCollection?.content === collections) {
      navigateToPage(id);
      return;
    }
    setSaveModal(true);
    const collectionContents = collections.map((lesson, index) => {
      return {
        id: lesson.id,
        multiCollectionId: id,
        type: lesson.type,
        position: index + 1
      }
    })
    setSavingLessons(collectionContents)
  }, [id, navigateToPage, currentCollection?.getMultiCollection?.content]);

  const handleUpdateCollection = useCallback(() => {
    saveMultiCollection({
      variables: {
        input: savingLessons
      }
    })
  }, [saveMultiCollection, savingLessons])

  const handleLeaveUpdate = useCallback(() => {
    setSaveModal(false);
    navigateToPage(id);
  }, [navigateToPage, id])

  return (
    <>
      <Header />
      <div className={styles.customCollectionPage}>
        <CustomizeMultiCollections
          collection={currentCollection?.getMultiCollection}
          loadingCollection={collectionLoading}
          handleSave={handleSave}
          handleExit={handleExit}
          isDisabled={loading}
        />
      </div>
      {saveModal && <NormalModal
        className={styles.saveModal}
        open={saveModal}
        onClose={() => setSaveModal(false)}
        title={<div style={{color: "#FF656E"}}>Unsaved changes</div>} 
        description={<div style={{color: "#FF656E"}}>You are about to leave this page without saving. All changes will be lost.</div>} 
        leftButton={<Button onClick={handleLeaveUpdate} label='LEAVE WITHOUT SAVING' className={styles.exitButton} />} 
        rightButton={<Button onClick={handleUpdateCollection} label='SAVE CHANGES' className={styles.saveButton} leftIcon={loading ? <Spinner width={20} height={20} /> : undefined} />}/>}
    </>
  );
}

export default MultiCollectionCustomize;
